import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../Components/Header";
import Pagetitle from "../Components/Pagetitle";
import Footer from "../Components/Footer";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import api, { postData } from "../Api";

const ComplaintPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  const [formData, setFormData] = useState({
    order_id: "",
    customer_number: "",
    complaint_text: "",
    images: [],
  });

  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("success");
  const [complaints, setComplaints] = useState([]);
  const [guestCustomerNumber, setGuestCustomerNumber] = useState("");
  const [orders, setOrders] = useState([]);

  // Load user + fetch complaints
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));

    if (storedUser && storedUser.phone) {
      setUser(storedUser);
      setFormData((prev) => ({
        ...prev,
        customer_number: storedUser.phone,
      }));
      fetchUserComplaints(storedUser.phone);
    }
  }, []);

  const fetchOrders = async (phone) => {
    try {
      const response = await api.get(`/my-orders/${phone}`);

      if (response.data.success) {
        const orderList = response.data.data || [];
        setOrders(orderList);

        if (orderList.length === 1) {
          setFormData((prev) => ({
            ...prev,
            order_id: orderList[0].order_id,
          }));
        }
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrders([]);
    }
  };

  // Fetch Complaints
  const fetchUserComplaints = async (customer_number) => {
    try {
      const data = await postData("/user-complaints", { customer_number });

      if (data.success) {
        setComplaints(data.complaints);
      } else {
        console.error(data.message);
      }
    } catch (error) {
      console.error("Error fetching complaints:", error);
    }
  };

  const handleGuestComplaintSearch = (e) => {
    e.preventDefault();

    if (!guestCustomerNumber.trim()) {
      setModalMessage("Please enter your mobile number to fetch complaints.");
      setModalType("error");
      setShowModal(true);
      return;
    }

    fetchUserComplaints(guestCustomerNumber.trim());
  };

  // Form Change Handlers
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "customer_number") {
      const phone = value.trim();

      if (/^\d{10}$/.test(phone)) {
        fetchOrders(phone);
        fetchUserComplaints(phone);
      } else {
        setOrders([]);
        setFormData((prev) => ({ ...prev, order_id: "" }));
      }
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 2);
    setFormData((prev) => ({ ...prev, images: files }));
  };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const closeModal = () => setShowModal(false);

  // Submit Complaint (with FormData)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const fd = new FormData();
      fd.append("order_id", formData.order_id);
      fd.append("customer_number", formData.customer_number);
      fd.append("complaint_text", formData.complaint_text);

      formData.images.forEach((file, index) => {
        fd.append(`images[${index}]`, file);
      });

      const response = await api.post("/complaint", fd, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const data = response.data;

      if (data.success) {
        setModalMessage("Complaint submitted successfully!");
        setModalType("success");
        setShowModal(true);

        setFormData({
          order_id: "",
          customer_number: user ? user.phone : "",
          complaint_text: "",
          images: [],
        });
      } else {
        setModalMessage(data.message || "Failed to submit complaint!");
        setModalType("error");
        setShowModal(true);
      }
    } catch (error) {
      console.error("Complaint submission error:", error);
      setModalMessage(error.message || "Something went wrong!");
      setModalType("error");
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="container-fluid">
      <Header />
      <Pagetitle />
      <div className="container my-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="border p-3 rounded bg-white shadow-sm">
              <div className="text-center p-4">
                <h3 className="mt-2">Complaint Form</h3>
                <hr />
              </div>
              <form onSubmit={handleSubmit}>
                <div className="mb-3 text-start">
                  <label className="form-label">
                    Mobile No. जिस से आप ने order करा है
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="customer_number"
                    value={formData.customer_number}
                    onChange={handleChange}
                    placeholder="Enter 10-digit Mobile Number"
                    maxLength={10}
                    inputMode="numeric"
                    required
                  />
                </div>
                <div className="mb-3 text-start">
                  <label className="form-label">
                    Order ID (जिस Order से Complaint है)
                  </label>

                  <select
                    className="form-select"
                    name="order_id"
                    value={formData.order_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select Order --</option>

                    {orders.map((order) => {
                      const formattedDate = new Date(
                        order.created_at,
                      ).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      });

                      return (
                        <option key={order.order_id} value={order.order_id}>
                          {order.order_id} — {formattedDate}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="mb-3 text-start">
                  <label className="form-label">
                    अपनी परेशानी नीचे Type करे
                  </label>
                  <textarea
                    className="form-control"
                    name="complaint_text"
                    value={formData.complaint_text}
                    onChange={handleChange}
                    placeholder="यहां अपनी समस्या लिखें..."
                    rows="4"
                    required
                  ></textarea>
                </div>
                <div className="mb-3 text-start">
                  <label className="form-label">Upload Images (Max 2)</label>
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                  />
                  {formData.images.length > 0 && (
                    <div className="mt-2 d-flex flex-wrap gap-2">
                      {formData.images.map((img, idx) => (
                        <div
                          key={idx}
                          className="position-relative"
                          style={{ width: "100px", height: "100px" }}
                        >
                          <img
                            src={URL.createObjectURL(img)}
                            alt={`preview-${idx}`}
                            className="img-thumbnail w-100 h-100 object-fit-cover"
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 p-1 rounded-circle"
                            onClick={() => removeImage(idx)}
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  className="btn-style1 w-100"
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Submit Complaint"}
                </button>
              </form>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="border p-3 rounded bg-white shadow-sm">
              <div className="text-center p-3">
                <h4 className="fw-semibold mb-0">Your Complaints</h4>
                <hr className="mt-2 mb-3" />
              </div>

              {!user && (
                <form onSubmit={handleGuestComplaintSearch} className="mb-3">
                  <div className="mb-2 text-start">
                    <label className="form-label">
                      Enter Your Mobile Number
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Your mobile number"
                      value={guestCustomerNumber}
                      onChange={(e) => setGuestCustomerNumber(e.target.value)}
                      required
                    />
                  </div>
                  <button type="submit" className="btn-style1 w-100">
                    Search Complaints
                  </button>
                  <hr className="mt-3 mb-0" />
                </form>
              )}

              {complaints.length === 0 ? (
                <p className="text-center mt-3 text-muted">
                  No complaints found.
                </p>
              ) : (
                complaints.map((complaint, idx) => {
                  const formattedDate = new Date(
                    complaint.created_at,
                  ).toLocaleString("en-IN", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  });

                  let badgeClass = "bg-secondary";
                  if (complaint.status === "pending")
                    badgeClass = "bg-warning text-dark";
                  else if (complaint.status === "in_progress")
                    badgeClass = "bg-primary";
                  else if (complaint.status === "resolved")
                    badgeClass = "bg-success";
                  else if (complaint.status === "rejected")
                    badgeClass = "bg-danger";

                  return (
                    <div
                      key={idx}
                      className="border rounded p-3 mb-3 bg-light position-relative shadow-sm"
                    >
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="fw-semibold small">
                          Order ID: {complaint.order_id}
                        </span>
                        <span className={`badge ${badgeClass} text-capitalize`}>
                          {complaint.status ?? "pending"}
                        </span>
                      </div>
                      <p className="mb-1 small text-muted">
                        <strong>Date:</strong> {formattedDate}
                      </p>
                      <p className="mb-2 small text-secondary">
                        <strong>Complaint:</strong> {complaint.complaint_text}
                      </p>
                      {complaint.reply && (
                        <div className="alert alert-success p-2 mb-2 small">
                          <strong>Admin Reply:</strong> {complaint.reply}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content py-md-4 py-3 px-3 rounded-5 position-relative text-center">
              <button
                type="button"
                className="btn-close position-absolute top-0 end-0 m-3"
                onClick={closeModal}
                aria-label="Close"
              ></button>
              <div className="modal-body">
                <div className="popup-icon mb-3">
                  {modalType === "success" ? (
                    <FaCheckCircle size={60} color="green" />
                  ) : (
                    <FaTimesCircle size={60} color="red" />
                  )}
                </div>
                <h5>{modalMessage}</h5>
              </div>
              <div className="text-center pb-3">
                <button
                  type="button"
                  className="btn-style1 mt-2"
                  onClick={() => {
                    closeModal();
                    if (modalType === "success") {
                      navigate("/");
                    }
                  }}
                >
                  {modalType === "success" ? "Go to Dashboard" : "Close"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </main>
  );
};

export default ComplaintPage;
